import { useEffect, useRef, useState } from 'react';

import {
	AdyenCheckout,
	ApplePay,
	Card,
	Dropin,
	GooglePay
} from '@adyen/adyen-web';

import adyenModel from '@/models/adyen';
import paymentModel, { PAYMENT_STATUS } from '@/models/payment';
import paymentMethodModel from '@/models/paymentMethod';
import subscriptionModel from '@/models/subscription';
import { isIOS, isMacOS, retryOn404 } from '@/utils';

import { DEFAULT_CURRENCY, STATUS } from '../../constants';
import { getFullLocale, waitForResourceCondition } from '../../utils';
import {
	EXTRA_ERRORS_MAP,
	HANDLER_ID_PARAM,
	REFULSAL_STATUS_CODES_MAP,
	RESULT_CODES,
	SECURE_REDIRECT_PARAM
} from '../constants';
import { waitForCardFormReady } from '../utils';

export default function useAdyenCheckout({
	showPayButton = true,
	priceId,
	mainPriceId,
	currency,
	attachPaymentMethod,
	onSuccess,
	onError,
	locale: localeProp = 'en',
	paymentMethodNamesMapping = {},
	translations = {},
	onlyAttachAndSwitch = false,
	preload = false
}) {
	const componentRef = useRef(null);
	const containerRef = useRef(null);
	const redirectHandledRef = useRef(false);
	const handlerIdRef = useRef(null);
	const submitDataRef = useRef(null);
	const dropinRef = useRef(null);

	const [isReady, setIsReady] = useState(false);
	const [isPaymentFormReady, setIsPaymentFormReady] = useState(false);
	const [isValid, setIsValid] = useState(false);
	const [isRedirecting, setIsRedirecting] = useState(false);
	const [paymentError, setPaymentError] = useState('');
	const [isActionMode, setIsActionMode] = useState(false);
	const [status, setStatus] = useState(STATUS.INITIAL);
	const [isResolving3ds, setIsResolving3ds] = useState(false);

	const locale = getFullLocale(localeProp);

	function onReady() {
		setIsReady(true);
	}

	function getSearchParam(key) {
		const params = new URLSearchParams(window.location.search);

		return params.get(key);
	}

	function getRedirectResult() {
		const params = new URLSearchParams(window.location.search);

		return params.get(SECURE_REDIRECT_PARAM);
	}

	function getDebugMode() {
		const params = new URLSearchParams(window.location.search);

		if (!import.meta.env.DEV) return false;

		return params.get('debug') === 'true';
	}

	function getHandlerIdFromUrl() {
		return getSearchParam(HANDLER_ID_PARAM);
	}

	function cleanUrl() {
		window.history.replaceState({}, document.title, window.location.pathname);
	}

	// Keep card expanded when wallet buttons are clicked.
	function activateCardPaymentMethod() {
		const schemePm = dropinRef.current?.paymentMethodElements?.find(item => {
			const { _id: id } = item;

			return id?.includes('scheme');
		});

		dropinRef.current?.dropinRef?.setActivePaymentMethod?.(schemePm);
	}

	function handleWalletError() {
		activateCardPaymentMethod();
	}

	async function waitForPaymentOrSetupResult({ handlerId }) {
		if (!handlerId) {
			throw new Error('Missing handlerId');
		}

		if (attachPaymentMethod) {
			const result = await retryOn404(() =>
				paymentMethodModel.getById(handlerId)
			);

			return {
				type: 'SETUP_COMPLETED',
				data: result
			};
		}

		const result = await waitForResourceCondition(
			() => paymentModel.getByHandlerId(handlerId),
			res => res.status === PAYMENT_STATUS.PAID
		);

		return {
			type: 'PAYMENT_COMPLETED',
			data: result
		};
	}

	function getPaymentMethodConfiguration({
		session,
		type,
		config = {},
		overrides = {}
	}) {
		const paymentMethodConfig =
			session.paymentMethodsResponse.paymentMethods.find(
				pm => pm.type === type
			)?.configuration || {};

		return {
			countryCode: session.countryCode.toUpperCase(),
			configuration: {
				...paymentMethodConfig,
				...config
			},
			onError: handleWalletError,
			...overrides
		};
	}

	async function fetchCheckoutSession() {
		const { handlerData } = await paymentMethodModel.update({
			priceId,
			currency,
			attachPaymentMethod
		});

		return handlerData;
	}

	async function createCheckout({ paymentMethodsResponse, amount, ...rest }) {
		const parsedAmount = {
			...amount,
			currency: (amount.currency || DEFAULT_CURRENCY).toUpperCase()
		};

		return AdyenCheckout({
			clientKey: import.meta.env.VITE_ADYEN_CLIENT_KEY,
			amount: parsedAmount,
			...rest,
			paymentMethodsResponse: {
				...paymentMethodsResponse,
				paymentMethods: paymentMethodsResponse.paymentMethods.map(item => {
					if (paymentMethodNamesMapping[item.type]) {
						return {
							...item,
							name: paymentMethodNamesMapping[item.type]
						};
					}

					return item;
				})
			},
			environment: import.meta.env.VITE_ADYEN_ENV || 'test',
			locale,
			translations: {
				[locale]: translations
			},

			onChange: async state => {
				setIsValid(state.isValid);

				if (paymentError) {
					setPaymentError('');
				}

				submitDataRef.current = null;
			},

			onSubmit: async (state, _, actions) => {
				try {
					setPaymentError('');
					setStatus(STATUS.PROCESSING);

					submitDataRef.current = null;

					const apiCall = attachPaymentMethod
						? adyenModel.savePayment.bind(adyenModel)
						: subscriptionModel.subscribe.bind(subscriptionModel);

					const payload = attachPaymentMethod
						? {
								currency,
								handlerData: { state: state.data }
							}
						: {
								priceId,
								mainPriceId,
								currency,
								onlyAttachAndSwitch,
								handlerData: {
									amount,
									...rest,
									state: state.data
								}
							};

					const { handlerData: submitHandlerData } =
						await apiCall(payload);

					submitDataRef.current = submitHandlerData;

					handlerIdRef.current = submitHandlerData?.pspReference || null;

					if (!submitHandlerData?.resultCode) {
						actions.reject();
						return;
					}

					actions.resolve(submitHandlerData);
				} catch (err) {
					actions.reject();
					onError?.(err);
				}
			},

			// 3DS
			onAdditionalDetails: async (state, _, actions) => {
				setIsResolving3ds(true);

				try {
					const result = await adyenModel.redirectResult({
						handlerData: state
					});

					actions.resolve(result);
				} catch (err) {
					actions.reject();
					onError?.(err);
				}
			},

			onPaymentCompleted: async result => {
				try {
					setIsResolving3ds(false);
					setIsActionMode(false);

					let handlerId = handlerIdRef.current;

					if (!handlerId) {
						handlerId = getHandlerIdFromUrl();
					}

					if (!handlerId) {
						onSuccess?.(result);
						return;
					}

					const finalResult = await waitForPaymentOrSetupResult({
						handlerId
					});

					submitDataRef.current = null;

					setPaymentError('');
					setStatus(STATUS.SUCCESS);

					onSuccess?.(finalResult);
				} catch (err) {
					setStatus(STATUS.ERROR);
					onError?.(err);
				}
			},

			onPaymentFailed: (result, component) => {
				setIsResolving3ds(false);
				setIsActionMode(false);
				setStatus(STATUS.ERROR);

				const submitData = submitDataRef.current;

				if (result?.resultCode === RESULT_CODES.REFUSED) {
					component.setStatus('ready');

					setPaymentError(
						REFULSAL_STATUS_CODES_MAP[submitData.refusalReasonCode] ||
							REFULSAL_STATUS_CODES_MAP.generic
					);

					return;
				}

				if (!result) {
					component.setStatus('ready');

					setPaymentError(EXTRA_ERRORS_MAP.generic);

					return;
				}

				onError?.(result);
			},

			onActionHandled: () => {
				setStatus(STATUS.ACTION_REQUIRED);
				setIsActionMode(true);
			},

			onError: err => {
				setIsResolving3ds(false);
				setIsActionMode(false);

				onError?.(err);
			}
		});
	}

	async function handleRedirect() {
		const redirectResult = getRedirectResult();

		if (!redirectResult || redirectHandledRef.current) return;

		redirectHandledRef.current = true;

		setIsRedirecting(true);

		try {
			const result = await adyenModel.redirectResult({
				handlerData: {
					details: { redirectResult }
				}
			});

			const handlerId = result?.pspReference || getHandlerIdFromUrl();

			if (!handlerId) {
				onSuccess?.(result);
				return;
			}

			const finalResult = await waitForPaymentOrSetupResult({
				handlerId
			});

			cleanUrl();

			onSuccess?.(finalResult);
		} catch (err) {
			onError?.(err);
		} finally {
			setIsRedirecting(false);
		}
	}

	useEffect(() => {
		handleRedirect();
	}, []);

	useEffect(() => {
		let instance;
		let isMounted = true;

		const redirectResult = getRedirectResult();

		if (redirectResult) return;

		setIsPaymentFormReady(false);
		setIsReady(false);

		async function init() {
			try {
				const createCheckoutSession =
					window.createCheckoutSessionPromise && preload
						? () => window.createCheckoutSessionPromise
						: fetchCheckoutSession;

				// Init checkout session
				const session = await createCheckoutSession({
					priceId,
					currency,
					attachPaymentMethod
				});

				if (!isMounted) return;

				// Config
				const checkout = await createCheckout(session);

				if (!isMounted) return;

				const paymentMethodComponents = [Card];
				const instantPaymentTypes = [];

				if (isIOS()) {
					paymentMethodComponents.push(ApplePay);
					instantPaymentTypes.push('applepay');
				} else if (!isMacOS()) {
					paymentMethodComponents.push(GooglePay);
					instantPaymentTypes.push('googlepay');
				}

				instance = new Dropin(checkout, {
					showPayButton,
					onReady,
					paymentMethodComponents,
					instantPaymentTypes,
					paymentMethodsConfiguration: {
						applepay: getPaymentMethodConfiguration({
							session,
							type: 'applepay',
							overrides: {
								buttonSizeMode: 'static',
								buttonType: 'plain',
								onClick(resolve) {
									resolve();
									activateCardPaymentMethod();
								}
							}
						}),
						googlepay: getPaymentMethodConfiguration({
							session,
							type: 'googlepay',
							overrides: {
								buttonSizeMode: 'static',
								buttonType: 'short',
								existingPaymentMethodRequired: false,
								onClick(resolve) {
									resolve();
									activateCardPaymentMethod();
								}
							}
						}),

						...(getDebugMode()
							? {
									card: {
										hasHolderName: true,
										holderNameRequired: false,
										challengeWindowSize: '05'
									}
								}
							: {})
					}
				});

				dropinRef.current = instance;

				instance.mount(containerRef.current);

				await waitForCardFormReady(containerRef.current);

				if (!isMounted) return;

				setIsPaymentFormReady(true);
			} catch (err) {
				onError?.(err);
			} finally {
				window.createCheckoutSessionPromise = null;
			}
		}

		init();

		return () => {
			isMounted = false;
			instance?.unmount?.();
			setPaymentError('');
		};
	}, [priceId, currency, attachPaymentMethod, showPayButton]);

	return {
		containerRef,
		componentRef,
		isReady,
		isPaymentFormReady,
		isValid,
		isRedirecting,
		paymentError,
		isActionMode,
		status,
		isResolving3ds
	};
}
